import type { SortModelItem } from 'ag-grid-enterprise';

import type { SearchableSelectConfig } from '@/components/SearchableSelect/SearchableSelect.types';

export const CASES_TABLE_DEFAULT_SORT_MODEL: SortModelItem[] = [
  {
    colId: 'soc_last_updated_at',
    sort: 'desc'
  }
];

export const CASES_INTERNAL_TABLE_LABEL = 'CasesInternalTable';
export const CASES_TABLE_LABEL = 'CasesTable';

export const CASE_PRIORITY_OPTIONS = [
  { label: 'P1', value: '1', id: 1 },
  { label: 'P2', value: '2', id: 2 },
  { label: 'P3', value: '3', id: 3 },
  { label: 'P4', value: '4', id: 4 },
  { label: 'P5', value: '5', id: 5 }
];

export const CASE_PRIORITY_LABEL_OPTIONS = [
  { label: 'P1', value: 'P1 - Critical', id: 1 },
  { label: 'P2', value: 'P2 - High', id: 2 },
  { label: 'P3', value: 'P3 - Medium', id: 3 },
  { label: 'P4', value: 'P4 - Low', id: 4 },
  { label: 'P5', value: 'P5 - Info', id: 5 }
];

// Values match the backend's computed `priority` field (getCasePriority-normalized P1-P5), unlike
// CASE_PRIORITY_OPTIONS' numeric ids which back the ticket__priority_id column filter.
export const CASE_COMPUTED_PRIORITY_OPTIONS = [
  { label: 'P1', value: 'P1', id: 1 },
  { label: 'P2', value: 'P2', id: 2 },
  { label: 'P3', value: 'P3', id: 3 },
  { label: 'P4', value: 'P4', id: 4 },
  { label: 'P5', value: 'P5', id: 5 }
];

export const CASE_CATEGORY_OPTIONS = [
  { label: 'Malware', value: 'Malware' },
  { label: 'Phishing', value: 'Phishing' },
  { label: 'Ransomware', value: 'Ransomware' },
  { label: 'Data Leak', value: 'Data Leak' },
  { label: 'Unauthorized Access', value: 'Unauthorized Access' },
  { label: 'Credential Theft', value: 'Credential Theft' },
  { label: 'Insider Threat', value: 'Insider Threat' },
  { label: 'SQL Injection', value: 'SQL Injection' }
];

export const CASE_SCOPE_VERDICT_OPTIONS = [
  { label: 'Threat Mitigated', value: 'Threat Mitigated', id: 1 },
  { label: 'False Positive', value: 'False Positive', id: 2 },
  { label: 'Benign Activity', value: 'Benign Activity', id: 3 },
  { label: 'Inconclusive', value: 'Inconclusive', id: 4 },
  { label: 'Duplicate Ticket', value: 'Duplicate Ticket', id: 5 },
  { label: 'No Response', value: 'No Response', id: 6 },
  { label: 'Escalated to DFIR', value: 'Escalated to DFIR', id: 7 }
];

export const CASE_VERDICT_OPTIONS = [
  {
    label: 'True Positive',
    value: '1',
    id: 1
  },
  {
    label: 'False Positive',
    value: '2',
    id: 2
  },
  {
    label: 'Benign Activity',
    value: '3',
    id: 3
  },
  {
    label: 'Closed',
    value: '4',
    id: 4
  },
  {
    label: 'Acknowledged',
    value: '5',
    id: 5
  },
  {
    label: 'Remediated',
    value: '6',
    id: 6
  }
];

export const CASE_AI_VERDICT_OPTIONS = [
  {
    label: 'Escalate Threat',
    value: 'escalate_threat'
  },
  {
    label: 'Dismiss False Positive',
    value: 'dismiss_false_positive'
  },
  {
    label: 'Dismiss Duplicate',
    value: 'dismiss_duplicate'
  },
  {
    label: 'Unknown',
    value: 'unknown'
  }
];

export enum CASE_STATUS {
  NEW = 'New',
  IN_PROGRESS = 'In Progress',
  CLOSED = 'Closed',
  RESOLVED = 'Resolved'
}

export const CASE_STATUS_COLORS = {
  [CASE_STATUS.NEW]: 'warning',
  [CASE_STATUS.IN_PROGRESS]: 'info',
  [CASE_STATUS.CLOSED]: 'success',
  [CASE_STATUS.RESOLVED]: 'success'
} as const;

export enum CASE_AI_STATUS {
  LOADING = 'Loading',
  FAILED = 'Failed',
  AI_PROCESSING = 'AI Processing',
  AI_ERROR = 'AI Error',
  COMPLETED = 'Completed',
  QUEUED = 'Queued',
  THROTTLED = 'Throttled',
  ELIGIBLE = 'Eligible',
  NA = 'N/A',
  SUPPRESSED = 'Suppressed'
}

export const CASE_MANUAL_AI_STATUSES = [
  CASE_AI_STATUS.COMPLETED,
  CASE_AI_STATUS.ELIGIBLE,
  CASE_AI_STATUS.THROTTLED,
  CASE_AI_STATUS.NA,
  CASE_AI_STATUS.FAILED,
  CASE_AI_STATUS.AI_ERROR
];

export const CASE_AI_STATUS_COLORS = {
  [CASE_AI_STATUS.LOADING]: 'info',
  [CASE_AI_STATUS.FAILED]: 'error',
  [CASE_AI_STATUS.AI_PROCESSING]: 'info',
  [CASE_AI_STATUS.AI_ERROR]: 'warning',
  [CASE_AI_STATUS.COMPLETED]: 'success',
  [CASE_AI_STATUS.QUEUED]: 'info',
  [CASE_AI_STATUS.THROTTLED]: 'warning',
  [CASE_AI_STATUS.ELIGIBLE]: 'info',
  [CASE_AI_STATUS.NA]: 'default',
  [CASE_AI_STATUS.SUPPRESSED]: 'warning'
} as const;

export enum CASE_ACTIVITY_TYPE {
  SOC_REVIEW = 'soc_review'
}

export enum CASE_REVIEW_STATUS {
  PENDING = 'Pending',
  COMPLETED = 'Completed',
  NA = 'N/A'
}

export const CASE_SOC_REVIEW_EXTERNAL_COLORS = {
  [CASE_REVIEW_STATUS.PENDING]: 'info',
  [CASE_REVIEW_STATUS.COMPLETED]: 'success',
  [CASE_REVIEW_STATUS.NA]: 'unknown'
} as const;

export const CASE_SOC_REVIEW_COLORS = {
  'Properly Escalated': 'success',
  'Improperly Escalated': 'error',
  'Properly Dismissed': 'success',
  'Improperly Dismissed': 'error'
} as const;

export const CASE_STATUS_OPTIONS = [
  { label: CASE_STATUS.NEW, value: CASE_STATUS.NEW },
  { label: CASE_STATUS.IN_PROGRESS, value: CASE_STATUS.IN_PROGRESS },
  { label: CASE_STATUS.CLOSED, value: CASE_STATUS.CLOSED },
  { label: CASE_STATUS.RESOLVED, value: CASE_STATUS.RESOLVED }
];

export const CASE_MANAGED_OPTIONS = [
  { id: 1, label: 'Managed', value: 'true' },
  { id: 2, label: 'Not Managed', value: 'false' }
];

export const CASE_AI_STATUS_OPTIONS = [
  { label: CASE_AI_STATUS.LOADING, value: 'loading' },
  { label: CASE_AI_STATUS.FAILED, value: 'failed' },
  { label: CASE_AI_STATUS.AI_PROCESSING, value: 'ai_processing' },
  { label: CASE_AI_STATUS.AI_ERROR, value: 'ai_error' },
  { label: CASE_AI_STATUS.COMPLETED, value: 'completed' },
  { label: CASE_AI_STATUS.QUEUED, value: 'queued' },
  { label: CASE_AI_STATUS.THROTTLED, value: 'throttled' },
  { label: CASE_AI_STATUS.ELIGIBLE, value: 'eligible' },
  { label: CASE_AI_STATUS.NA, value: 'n/a' },
  { label: CASE_AI_STATUS.SUPPRESSED, value: 'suppressed' }
];

export const CASE_REVIEWED_OPTIONS = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' }
];

export const CASE_SOC_REVIEW_OPTIONS = [
  { label: 'Properly Escalated', value: 'Properly Escalated' },
  { label: 'Improperly Escalated', value: 'Improperly Escalated' },
  { label: 'Properly Dismissed', value: 'Properly Dismissed' },
  { label: 'Improperly Dismissed', value: 'Improperly Dismissed' }
];

export const CASE_SEARCHABLE_SELECT_FILTERS: SearchableSelectConfig[] = [
  {
    field: 'ai_status',
    title: 'Status',
    config: {
      searchable: false,
      showCounts: true
    },
    filterOptions: CASE_AI_STATUS_OPTIONS,
    hidden: false
  },
  {
    field: 'priority',
    title: 'Priority',
    config: {
      searchable: false,
      showCounts: true
    },
    filterOptions: CASE_COMPUTED_PRIORITY_OPTIONS,
    hidden: false
  },
  {
    field: 'ai_verdict',
    title: 'AI Verdict',
    config: {
      searchable: false,
      showCounts: true
    },
    filterOptions: CASE_AI_VERDICT_OPTIONS,
    hidden: false
  },
  {
    field: 'scope_verdict',
    title: 'Ticket Resolution',
    config: {
      searchable: false,
      showCounts: true
    },
    filterOptions: CASE_SCOPE_VERDICT_OPTIONS
  },
  {
    field: 'customer_category',
    title: 'Category',
    config: {
      searchable: true,
      showCounts: true
    },
    filterOptions: CASE_CATEGORY_OPTIONS
  }
];

export const INTERNAL_CASE_SEARCHABLE_SELECT_FILTERS: SearchableSelectConfig[] =
  [
    {
      field: 'ai_managed',
      title: 'Managed',
      config: {
        searchable: false,
        showCounts: true,
        variant: 'buttongroup'
      },
      filterOptions: CASE_MANAGED_OPTIONS
    },
    {
      field: 'soc_status',
      title: 'SOC Status',
      config: {
        searchable: false,
        showCounts: true
      },
      filterOptions: CASE_STATUS_OPTIONS
    },
    {
      field: 'ai_status',
      title: 'AI Status',
      config: {
        searchable: false,
        showCounts: true
      },
      filterOptions: CASE_AI_STATUS_OPTIONS,
      hidden: false
    },
    {
      field: 'soc_verdict',
      title: 'SOC Verdict',
      config: {
        searchable: false,
        showCounts: true
      },
      filterOptions: []
    },
    {
      field: 'soc_review_message',
      title: 'SOC Reviewed',
      config: {
        searchable: true,
        showCounts: true
      },
      filterOptions: CASE_SOC_REVIEW_OPTIONS
    },
    {
      field: 'ai_verdict',
      title: 'AI Verdict',
      config: {
        searchable: false,
        showCounts: true
      },
      filterOptions: CASE_AI_VERDICT_OPTIONS,
      hidden: false
    },
    {
      field: 'scope_verdict',
      title: 'Ticket Verdict',
      config: {
        searchable: false,
        showCounts: true
      },
      filterOptions: CASE_SCOPE_VERDICT_OPTIONS
    },
    {
      field: 'soc_priority',
      title: 'Queue Priority',
      config: {
        searchable: false,
        showCounts: true
      },
      filterOptions: CASE_PRIORITY_LABEL_OPTIONS,
      hidden: false
    },
    {
      field: 'ticket__priority_id',
      title: 'Ticket Priority',
      config: {
        searchable: false,
        showCounts: true
      },
      filterOptions: CASE_PRIORITY_OPTIONS
    },
    {
      field: 'ai_priority',
      title: 'AI Priority',
      config: {
        searchable: false,
        showCounts: true
      },
      filterOptions: []
    },
    {
      field: 'soc_category',
      title: 'SOC Category',
      config: {
        searchable: true,
        showCounts: true
      },
      filterOptions: CASE_CATEGORY_OPTIONS
    },
    {
      field: 'ai_category',
      title: 'AI Category',
      config: {
        searchable: true,
        showCounts: true
      },
      filterOptions: CASE_CATEGORY_OPTIONS
    }
  ];

export const CASE_AI_DESCRIPTIONS = [
  {
    label: 'No AI Status',
    description: 'AI analysis has not yet been initiated for this case.'
  },
  {
    label: 'AI Error',
    description: 'An error occurred during AI analysis of this case.'
  },
  {
    label: 'AI Processing',
    description: 'AI is actively analyzing this case.'
  },
  {
    label: 'Completed',
    description: 'AI analysis has been completed.'
  },
  {
    label: 'Eligible',
    description: 'Case is eligible for AI analysis.'
  },
  {
    label: 'Loading',
    description: 'AI analysis is being prepared for this case.'
  },
  {
    label: 'N/A',
    description: 'AI analysis is not applicable for this case.'
  },
  {
    label: 'Queued',
    description: 'AI analysis is queued and will be processed soon.'
  },
  {
    label: 'Throttled',
    description: 'AI analysis is temporarily delayed due to high demand.'
  },
  {
    label: 'Failed',
    description: 'AI analysis failed for this case.'
  },
  {
    label: 'Suppressed',
    description: 'AI analysis has been suppressed for this case.'
  }
];

export const CASES_GROUP_BY_FIELDS: string[] = [
  'soc_status',
  'ai_status',
  'soc_verdict',
  'ai_verdict',
  'ai_managed',
  'soc_review_message',
  'soc_priority',
  'ai_priority',
  'ticket__priority_id',
  'ticket__resolution_id',
  'ticket__ticket_status_id',
  'ticket__category_id',
  'ai_category',
  'soc_category',
  'scope_verdict',
  'priority',
  'customer_category'
];

export const CASES_SEARCH_FIELD_LABELS: Record<string, string> = {
  ai_status: 'Status',
  soc_case_title: 'Title',
  ai_verdict: 'Verdict',
  scope_verdict: 'Ticket Resolution',
  customer_category: 'Category',
  soc_id: 'ID',
  soc_case_extra_details: 'Extra Details'
};

export const CASES_INTERNAL_SEARCH_FIELD_LABELS: Record<string, string> = {
  soc_priority: 'Queue Priority',
  scope_verdict: 'Ticket Verdict'
};

export const CASES_HIDDEN_FIELDS: string[] = [
  'id',
  'organization__label',
  'organization__name'
];
